import React, { createContext, useContext, useReducer } from "react";
import { AnnouncedProps } from "./Announced.types";
import { AppAnnouncer } from "./AppAnnouncer";

interface AnnouncementsList {
    /**
     * List of announcements.
     */
    readonly announcements: readonly AnnouncedProps[];
}

interface AnnouncementsActions {
    /**
     * Type of actions we can dispatch to the AnnouncementsProvider.
     */
    readonly type: "push";
    /**
     * Announcement that's being dispatched to the AnnouncementsProvider.
     */
    readonly announcement: AnnouncedProps;
}

interface AnnouncementsContextType {
    /**
     * State of the AnnouncementsContext which is represented by a list of announcements.
     */
    readonly state: AnnouncementsList;
    /**
     * Action we're dispatching to the AnnouncementsContext.
     */
    readonly dispatch: React.Dispatch<AnnouncementsActions>;
}

interface AnnouncementsProviderProps {
    /**
     * Content to be rendered inside the AnnouncementsProvider
     */
    readonly children?: React.ReactNode;
    /**
     * Announcer component to be rendered in the AnnouncementsProvider
     */
    readonly announcedComponent: React.FC<AnnouncedProps>;
}

const AnnouncementsContext = createContext<AnnouncementsContextType | null>(null);

/**
 * Wrapped hook for using AnnouncementsStateContext.
 */
export const useAnnouncementsStateContext = (): AnnouncementsList => {
    const context = useContext(AnnouncementsContext);

    if (context === null) {
        throw new Error("AnnouncementsContext: Context is uninitialized. Confirm that your component is contained within an AnnouncementsContext.Provider component.");
    }

    return context.state;
};

/**
 * Wrapped hook for using AnnouncementsDispatchContext.
 */
export const useAnnouncementsDispatchContext = (): React.Dispatch<AnnouncementsActions> => {
    const context = useContext(AnnouncementsContext);

    if (context === null) {
        throw new Error("AnnouncementsContext: Context is uninitialized. Confirm that your component is contained within an AnnouncementsContext.Provider component.");
    }

    return context.dispatch;
};

/**
 * Context provider for Announcements
 */
export const AnnouncementsProvider: React.FC<AnnouncementsProviderProps> = ({
    children,
    announcedComponent
}) => {

    const reducer = (currentState: AnnouncementsList, action: AnnouncementsActions): AnnouncementsList => {
        switch (action.type) {
            case "push":
                return { announcements: currentState.announcements.concat(action.announcement) };
        }
    };

    const [state, dispatch] = useReducer(reducer, { announcements: [] });

    return (
        <AnnouncementsContext.Provider value={{ state, dispatch }}>
            {children}
            <AppAnnouncer announcedComponent={announcedComponent} />
        </AnnouncementsContext.Provider>
    );
};
